import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Browser } from '@capacitor/browser';
import { TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import saveAs from 'file-saver';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { NativeCheckService } from 'src/app/services/native-check.service';
import { ApiTransactionTypes, ApiUiTransactionTypes, GenerateDocumentLinkOut, SelectItemCheckbox } from 'src/app/services/rest-api/rest-api';
import { RestApiService } from 'src/app/services/rest-api/rest-api.service';
import { ToastNotificationService } from 'src/app/services/toast-notification/toast-notification.service';
import { UserService } from 'src/app/services/user/user.service';
import { ToastNotificationComponent } from 'src/app/shared/toast-notification/toast-notification.component';

interface TransactionsExportFormGroup {
  dateFrom: FormControl<dayjs.Dayjs>,
  dateTo: FormControl<dayjs.Dayjs>,
  transactionTypes: FormArray<FormControl<SelectItemCheckbox>>,
}


@Component({
  selector: 'libero-transactions-export-modal',
  templateUrl: './transactions-export-modal.component.html',
  styleUrl: './transactions-export-modal.component.scss',
  standalone: false,
})
export class TransactionsExportModalComponent implements OnChanges {
  @Input() public show: boolean | null = null;
  @Output() public exportFinish = new EventEmitter<void>();

  public get selectedCheckboxes(): SelectItemCheckbox[] {
    return this.transactionsExportForm?.value['transactionTypes']?.filter(checkbox => checkbox.value) ?? [];
  }

  public get availableCheckboxes(): SelectItemCheckbox[] {
    return this.transactionsExportForm?.controls['transactionTypes'].value ?? [];
  }

  public transactionsExportForm?: FormGroup<TransactionsExportFormGroup> | null;
  public today = dayjs().startOf('day').toDate();
  public clientCreatedDate = dayjs(this.userService.loggedUser?.createdDate).toDate();

  public preloader = {
    generating: false,
  };

  public constructor(
    private toastService: ToastNotificationService,
    private loggerService: LoggerService,
    private userService: UserService,
    private restApiService: RestApiService,
    private nativeCheckService: NativeCheckService,
    private translate: TranslateService,
    private fb: FormBuilder,
  ) {}


  public ngOnChanges(changes: SimpleChanges): void {
    if(changes['show'].currentValue)  {
      this.initForm();
    } else {
      this.transactionsExportForm = null;
    }
  }

  private initForm(): void {
    this.clientCreatedDate = dayjs(this.userService.loggedUser?.createdDate).toDate();
    this.transactionsExportForm = new FormGroup<TransactionsExportFormGroup>({
      dateFrom: new FormControl(dayjs(this.userService.loggedUser?.createdDate), { nonNullable: true }),
      dateTo: new FormControl(dayjs(), { nonNullable: true }),
      transactionTypes: this.generateFormArray([
        {
          label: this.translate.instant('money.transactionTypes.deposits'),
          apiKey: ApiUiTransactionTypes.DEPOSIT,
          value: true,
        },
        {
          label: this.translate.instant('money.transactionTypes.withdraws'),
          apiKey: ApiUiTransactionTypes.WITHDRAWAL,
          value: true
        },
        {
          label: this.translate.instant('money.transactionTypes.purchases'),
          apiKey: ApiUiTransactionTypes.BUY,
          value: true
        },
        {
          label: this.translate.instant('money.transactionTypes.sells'),
          apiKey: ApiUiTransactionTypes.SELL,
          value: true
        },
        {
          label: this.translate.instant('money.transactionTypes.fees'),
          apiKey: ApiTransactionTypes.FEE,
          value: false
        },
        {
          label: this.translate.instant('money.transactionTypes.refunds'),
          apiKey: ApiTransactionTypes.OPF_MANAGEMENT_FEE_REFUND,
          value: false
        },
        {
          label: this.translate.instant('money.transactionTypes.dividends'),
          apiKey: ApiUiTransactionTypes.DIVIDEND,
          value: false
        },
        {
          label: this.translate.instant('money.transactionTypes.fx'),
          apiKey: ApiUiTransactionTypes.FX_TRANSACTION,
          value: false
        },
      ]),
    });
  }

  private generateFormArray(controlsData: SelectItemCheckbox[]): FormArray<FormControl<SelectItemCheckbox>> {
    const formArray = new FormArray<FormControl<SelectItemCheckbox>>([]);

    for (const checkbox of controlsData) {
      formArray.push(this.createCheckboxControl(checkbox));
    }

    return formArray;
  }

  private createCheckboxControl(checkbox: SelectItemCheckbox): FormControl<SelectItemCheckbox> {
    return this.fb.control({
      label: checkbox.label,
      apiKey: checkbox.apiKey,
      value: checkbox.value
    }, { nonNullable: true });
  }

  public generateTransactionsExportLink(): void {
    if(!this.transactionsExportForm || !this.userService.loggedUser) {
      this.loggerService.error('Attempt to open transactions export without data!');
      this.exportFinish.emit();
    } else {
      this.preloader.generating = true;
      this.restApiService.generateTransactionsExport(
        {
          clientId: this.userService.loggedUser.clientAccountId,
          optDateFrom: this.transactionsExportForm.value['dateFrom']?.format('YYYY-MM-DD') ?? '',
          optDateTo: this.transactionsExportForm.value['dateTo']?.format('YYYY-MM-DD') ?? '',
          transactionTypes: this.selectedTypes,
        }
      ).subscribe({
          next: (data) => this.transactionsLinkGenerated(data),
          error: (err) => this.transactionsLinkGenerationError(err),
      });
    }
  }

  private get selectedTypes(): ApiUiTransactionTypes[] {
    const types = this.selectedCheckboxes.map(checkbox => checkbox.apiKey) as ApiUiTransactionTypes[] ?? [];
    if(types.some(type => type === ApiUiTransactionTypes.OPF_MANAGEMENT_FEE_REFUND)) {
      types.push(ApiUiTransactionTypes.ROUNDING_RETURN);
    }
    return types;
  }

  private transactionsLinkGenerated(transactionsLink: GenerateDocumentLinkOut): void {
    if(this.nativeCheckService.isNativePlatform()) {
      Browser.open({ url: transactionsLink.link });
    } else {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', transactionsLink.link, true);
      xhr.responseType = 'blob';

      xhr.onload = () => {
        if (xhr.status === 200) {
          const blob = new Blob([xhr.response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(blob, transactionsLink.filename);
        } else {
          this.toastService.addNotification({
            title: 'toastNotifications.headers.error',
            message: 'toastNotifications.messages.documentError',
            type: 'warning',
            component: ToastNotificationComponent
          });
        }

        this.preloader.generating = false;
        this.exportFinish.emit();
      };

      xhr.send();
    }
  }

  public updateValidToValue(value: string): void {
    this.transactionsExportForm?.patchValue({ dateTo: dayjs(value) });
  }

  public updateValidFromValue(value: string): void {
    this.transactionsExportForm?.patchValue({ dateFrom: dayjs(value) });
  }

  private transactionsLinkGenerationError(error: unknown): void {
    this.loggerService.error(error);
    this.toastService.unknownApiError();
    this.preloader.generating = false;
  }
}
